/**
 * Dashboard Consolidado — vista de las 3 unidades como conjunto.
 * KPIs · Barras stacked por mes · Cards por unidad · Top tipos · Próximos eventos
 */
import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "../../App";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { Card, CardContent } from "../../components/ui/card";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend, CartesianGrid,
} from "recharts";
import { Calendar, TrendingUp, Users, DollarSign, Sparkles, RefreshCw, ChevronRight } from "lucide-react";

const fmtMoney = (v) => `$${Number(v || 0).toLocaleString("es-MX", { maximumFractionDigits: 0 })}`;

const RANGOS = [
  { key: "este_mes", label: "Este mes", days: 30 },
  { key: "trimestre", label: "Trimestre", days: 90 },
  { key: "semestre", label: "6 meses", days: 180 },
  { key: "anio", label: "12 meses", days: 365 },
];

const toISO = (d) => d.toISOString().slice(0, 10);

const DashboardConsolidado = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [rango, setRango] = useState("semestre");
  const [unidadesFiltro, setUnidadesFiltro] = useState(["petit", "kermesita", "bocatto"]);

  const fetch = useCallback(async () => {
    setLoading(true);
    try {
      const info = RANGOS.find(r => r.key === rango) || RANGOS[2];
      const hoy = new Date();
      const desde = new Date(); desde.setDate(hoy.getDate() - Math.floor(info.days / 2));
      const hasta = new Date(); hasta.setDate(hoy.getDate() + Math.floor(info.days / 2));
      const { data: d } = await axios.get(`${API}/dashboard/consolidado?desde=${toISO(desde)}&hasta=${toISO(hasta)}`);
      setData(d);
    } catch {
      toast.error("Error al cargar dashboard");
    } finally {
      setLoading(false);
    }
  }, [rango]);

  useEffect(() => { fetch(); }, [fetch]);

  const toggleUnidad = (slug) => {
    setUnidadesFiltro(prev => prev.includes(slug) ? prev.filter(x => x !== slug) : [...prev, slug]);
  };

  if (loading && !data) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="dash-loading">
        <div className="w-8 h-8 border-2 border-emerald-900 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!data) return null;

  const t = data.totales;
  const colors = data.colors;

  return (
    <div className="space-y-4 pb-16" data-testid="dashboard-consolidado">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="font-display text-2xl sm:text-3xl text-stone-900 flex items-center gap-2">
            <Sparkles className="w-6 h-6 text-purple-700" />
            Dashboard Celebra
          </h1>
          <p className="text-sm text-stone-600">Vista consolidada de Petit · Kermesita · Bocatto</p>
        </div>
        <Button variant="outline" size="sm" onClick={fetch} data-testid="btn-refresh">
          <RefreshCw className="w-4 h-4 mr-1" /> Actualizar
        </Button>
      </div>

      {/* Filtros de rango */}
      <div className="flex flex-wrap gap-2" data-testid="filtros-rango">
        {RANGOS.map(r => (
          <button
            key={r.key}
            onClick={() => setRango(r.key)}
            className={`px-3 py-1.5 rounded-full text-xs font-medium border transition ${
              rango === r.key ? "bg-emerald-900 text-white border-emerald-900" : "bg-white text-stone-700 border-stone-300 hover:border-emerald-500"
            }`}
            data-testid={`rango-${r.key}`}
          >
            {r.label}
          </button>
        ))}
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="kpis-grid">
        <Card className="border-emerald-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <span className="text-xs text-stone-500 uppercase tracking-wide">Eventos</span>
              <Calendar className="w-4 h-4 text-emerald-700" />
            </div>
            <p className="text-2xl font-bold text-emerald-900" data-testid="kpi-eventos">{t.eventos}</p>
          </CardContent>
        </Card>
        <Card className="border-amber-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <span className="text-xs text-stone-500 uppercase tracking-wide">Ingresos</span>
              <DollarSign className="w-4 h-4 text-amber-700" />
            </div>
            <p className="text-2xl font-bold text-amber-900" data-testid="kpi-ingresos">{fmtMoney(t.ingresos)}</p>
          </CardContent>
        </Card>
        <Card className="border-purple-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <span className="text-xs text-stone-500 uppercase tracking-wide">Días integrales</span>
              <Sparkles className="w-4 h-4 text-purple-700" />
            </div>
            <p className="text-2xl font-bold text-purple-900" data-testid="kpi-integrales">{t.dias_integral}</p>
          </CardContent>
        </Card>
        <Card className="border-stone-300">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <span className="text-xs text-stone-500 uppercase tracking-wide">Ocupación</span>
              <TrendingUp className="w-4 h-4 text-stone-700" />
            </div>
            <p className="text-2xl font-bold text-stone-900" data-testid="kpi-ocupacion">{t.porcentaje_ocupacion}%</p>
            <p className="text-[10px] text-stone-500">{t.dias_ocupados} de {data.rango.total_dias} días</p>
          </CardContent>
        </Card>
      </div>

      {/* Cards por unidad */}
      <div>
        <h2 className="font-display text-lg text-stone-800 mb-3">Rendimiento por unidad</h2>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {Object.entries(data.por_unidad).map(([slug, u]) => {
            const activo = unidadesFiltro.includes(slug);
            return (
              <Card
                key={slug}
                onClick={() => toggleUnidad(slug)}
                className={`cursor-pointer transition-all ${activo ? "shadow-md" : "opacity-50"}`}
                style={{ borderColor: u.color, borderWidth: 2 }}
                data-testid={`card-unidad-${slug}`}
              >
                <CardContent className="p-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-semibold text-sm" style={{ color: u.color }}>{u.nombre}</span>
                    <span className="w-3 h-3 rounded-full" style={{ background: u.color }} />
                  </div>
                  <div className="space-y-1 text-sm">
                    <div className="flex justify-between"><span className="text-stone-500">Eventos:</span><span className="font-medium">{u.eventos}</span></div>
                    <div className="flex justify-between"><span className="text-stone-500">Ingresos:</span><span className="font-medium">{fmtMoney(u.ingresos)}</span></div>
                    <div className="flex justify-between"><span className="text-stone-500">Días ocupados:</span><span className="font-medium">{u.dias_ocupados}</span></div>
                    <div className="flex justify-between"><span className="text-stone-500">Personas:</span><span className="font-medium">{u.personas}</span></div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
        <p className="text-[10px] text-stone-500 mt-2">Click en una unidad para incluirla/excluirla del gráfico.</p>
      </div>

      {/* Gráfico stacked por mes */}
      <Card className="border-stone-200">
        <CardContent className="p-4">
          <h3 className="font-display text-base text-stone-800 mb-3">Eventos por mes y unidad</h3>
          {(data.por_mes || []).length === 0 ? (
            <p className="text-sm text-stone-500 text-center py-8">Sin eventos en el rango seleccionado.</p>
          ) : (
            <div style={{ width: "100%", height: 300 }}>
              <ResponsiveContainer>
                <BarChart data={data.por_mes} margin={{ top: 5, right: 5, left: 0, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e7e5e4" />
                  <XAxis dataKey="etiqueta" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                  <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  {unidadesFiltro.includes("petit")     && <Bar dataKey="petit"     name="Celebra Petit" stackId="a" fill={colors.petit} />}
                  {unidadesFiltro.includes("kermesita") && <Bar dataKey="kermesita" name="Kermesita"     stackId="a" fill={colors.kermesita} />}
                  {unidadesFiltro.includes("bocatto")   && <Bar dataKey="bocatto"   name="Bocatto"       stackId="a" fill={colors.bocatto} />}
                  <Bar dataKey="integral" name="Integral (3 unidades)" stackId="b" fill={colors.integral} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Top tipos + Próximos */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Card className="border-stone-200">
          <CardContent className="p-4">
            <h3 className="font-display text-base text-stone-800 mb-3 flex items-center gap-2">
              <TrendingUp className="w-4 h-4 text-emerald-700" />
              Top tipos de evento
            </h3>
            {(data.top_tipos || []).length === 0 ? (
              <p className="text-sm text-stone-500">Sin datos.</p>
            ) : (
              <div className="space-y-2">
                {data.top_tipos.map(({ tipo, count }) => {
                  const max = data.top_tipos[0]?.count || 1;
                  const pct = (count / max) * 100;
                  return (
                    <div key={tipo} className="flex items-center gap-2" data-testid={`tipo-${tipo}`}>
                      <span className="text-sm text-stone-700 w-32 truncate">{tipo}</span>
                      <div className="flex-1 h-2 bg-stone-100 rounded-full overflow-hidden">
                        <div className="h-full bg-emerald-600" style={{ width: `${pct}%` }} />
                      </div>
                      <span className="text-sm font-medium text-stone-800 w-8 text-right">{count}</span>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="border-stone-200">
          <CardContent className="p-4">
            <h3 className="font-display text-base text-stone-800 mb-3 flex items-center gap-2">
              <Calendar className="w-4 h-4 text-purple-700" />
              Próximos 7 días
            </h3>
            {(data.proximos || []).length === 0 ? (
              <p className="text-sm text-stone-500">Sin eventos próximos.</p>
            ) : (
              <div className="space-y-2 max-h-56 overflow-y-auto">
                {data.proximos.map(ev => (
                  <button
                    key={ev.evento_id}
                    onClick={() => navigate(`/panel/eventos/${ev.evento_id}`)}
                    className="w-full text-left p-2 hover:bg-stone-50 rounded-lg border border-stone-100 flex items-center gap-2"
                    style={{ borderLeft: `3px solid ${ev.es_integral ? colors.integral : colors[(ev.unidades || [])[0]] || "#78716c"}` }}
                    data-testid={`proximo-${ev.evento_id}`}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium text-sm text-stone-900 truncate">{ev.nombre || ev.tipo}</span>
                        {ev.es_integral && <span className="text-[9px] px-1 py-0.5 rounded font-bold" style={{ background: `${colors.integral}20`, color: colors.integral }}>⭐ INTEGRAL</span>}
                      </div>
                      <p className="text-xs text-stone-500">{ev.fecha} · {ev.hora_inicio || "—"} · {fmtMoney(ev.total)}</p>
                    </div>
                    <ChevronRight className="w-4 h-4 text-stone-400" />
                  </button>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Footer resumen */}
      <div className="text-xs text-stone-500 text-center pt-4 flex items-center justify-center gap-4 flex-wrap">
        <span><Users className="w-3 h-3 inline mr-1" />{t.clientes_activos} clientes activos</span>
        <span>·</span>
        <span>{t.dias_libres} días libres restantes</span>
        <span>·</span>
        <span>Rango: {data.rango.desde} → {data.rango.hasta}</span>
      </div>
    </div>
  );
};

export default DashboardConsolidado;
